import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { capitalCase } from 'change-case';
import { catchError, concatMap } from 'rxjs';
import { ASSESMENT_STATUS_COLOR_MAP, baseEntityComparator } from '@models';
import { NumberUtils, RxUtils, Tr } from '@utils';
import { Assesment, AssesmentType, ParkArea, ParkAreaAssesmentService } from '@services';
import { DataTable } from '@components/common';
import { Divider, IconButton, Stack, Typography, TypographyProps } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CircleIcon from '@mui/icons-material/Circle';

const Body1: FC<TypographyProps> = ({ sx, ...props }) => <Typography variant="caption" sx={{ ...sx, wordBreak: 'keep-all' }} {...props} />;

export interface IParkAreaTableProps {
	parkAreas: ParkArea[];
	latestAssesment?: Assesment;
	onEdit?: (parkArea: ParkArea) => void;
	onDelete?: (parkArea: ParkArea) => void;
}

export const ParkAreaTable: FC<IParkAreaTableProps> = ({ parkAreas, latestAssesment, onEdit, onDelete }) => {
	const navigate = useNavigate();
	const columns = useMemo<ColumnDef<ParkArea>[]>(
		() =>
			(
				[
					{
						accessorKey: 'name',
						header: () => <Tr.Portal path="parking-area.name" />,
						enableSorting: false,
						cell: ({ row }) => <Body1>{row.original.name || ''}</Body1>
					},
					{
						accessorKey: 'access',
						header: () => <Tr.Portal path="parking-area.access-type" />,
						enableSorting: false,
						cell: ({ row }) => <Body1>{capitalCase(row.original.access || '')}</Body1>
					},
					{
						accessorKey: 'coverageAndAccessibility',
						header: () => <Tr.Portal path="parking-area.charging-location" />,
						enableSorting: false,
						cell: ({ row }) => <Body1>{capitalCase(row.original.coverageAndAccessibility || '')}</Body1>
					},
					{
						accessorKey: 'installationType',
						header: () => <Tr.Portal path="parking-area.installation-type" />,
						enableSorting: false,
						cell: ({ row }) => <Body1>{capitalCase(row.original.installationType || '')}</Body1>
					},
					{
						accessorKey: 'floorInstallation',
						header: () => <Tr.Portal path="parking-area.floor-installation" />,
						enableSorting: false,
						cell: ({ getValue }) => (
							<Body1>
								<Tr.Portal path={getValue() ? 'yes' : 'no'} />{' '}
							</Body1>
						)
					},
					{
						accessorKey: 'powerAvailableForCharging',
						header: () => <Tr.Portal path="parking-area.power-available" />,
						enableSorting: false,
						cell: ({ row }) => (
							<Body1>
								{NumberUtils.formatAsNumber(row.original.powerAvailableForCharging, { suffix: 'kW/h', decimalScale: 0 })}
							</Body1>
						)
					},
					{
						accessorKey: 'parkingSlotsNumber',
						header: () => <Tr.Portal path="parking-area.parking-spot-available" />,
						enableSorting: false,
						cell: ({ row }) => <Body1>{NumberUtils.formatAsNumber(row.original.parkingSlotsNumber, { decimalScale: 0 })}</Body1>
					},
					{
						accessorKey: 'currentACInstalled',
						header: () => <Tr.Portal path="parking-area.ac-installed" />,
						enableSorting: false,
						cell: ({ row }) => <Body1>{NumberUtils.formatAsNumber(row.original.currentACInstalled, { decimalScale: 0 })}</Body1>
					},
					{
						accessorKey: 'currentDCInstalled',
						header: () => <Tr.Portal path="parking-area.dc-installed" />,
						enableSorting: false,
						cell: ({ row }) => <Body1>{NumberUtils.formatAsNumber(row.original.currentDCInstalled, { decimalScale: 0 })}</Body1>
					},
					latestAssesment?.assesmentType === AssesmentType.TEST
						? {
								accessorKey: 'status',
								header: () => <Tr.Portal path="parking-area.status" />,
								enableSorting: false,
								cell: ({ row }) => (
									<CircleIcon
										sx={{
											cursor: latestAssesment ? 'pointer' : 'not-allowed',
											width: 16,
											height: 16,
											color: latestAssesment ? ASSESMENT_STATUS_COLOR_MAP[row.original.status] : 'grey.500'
										}}
										onClick={() => {
											if (latestAssesment) {
												const { id, siteId } = row.original;
												const parkAreaAssesmentService = new ParkAreaAssesmentService(id);
												const assesmentId = latestAssesment.id;
												RxUtils.promisify(
													parkAreaAssesmentService.getByAssesmentId(assesmentId).pipe(
														concatMap((res) => RxUtils.valueOrThrow(res, new Error('Not created yet'))),
														catchError(() => parkAreaAssesmentService.create({ assesmentId }))
													),
													() => navigate(`./${siteId}/parking-area/${id}/assesment/${assesmentId}/edit`)
												);
											}
										}}
									/>
								)
						  }
						: undefined,
					{
						accessorKey: 'id',
						header: () => '',
						enableSorting: false,
						cell: ({ row: { original } }) => (
							<Stack direction="row" spacing={2}>
								<IconButton
									size="small"
									sx={{ backgroundColor: 'primary.200', color: 'primary.main' }}
									onClick={() => onEdit?.(original)}
								>
									<EditIcon color="inherit" fontSize="small" />
								</IconButton>
								<IconButton
									size="small"
									sx={{ backgroundColor: 'primary.200', color: 'primary.main' }}
									onClick={() => onDelete?.(original)}
								>
									<DeleteForeverIcon color="inherit" fontSize="small" />
								</IconButton>
							</Stack>
						)
					}
				] as ColumnDef<ParkArea>[]
			).filter(Boolean),
		[onEdit, onDelete, latestAssesment]
	);

	return (
		<>
			<Divider light sx={{ paddingTop: 3.5 }} />
			<DataTable
				columns={columns}
				data={parkAreas || []}
				comparator={baseEntityComparator}
				variant="elevation"
				elevation={0}
				headerTypographyProps={{ variant: 'caption', fontWeight: 600 }}
				headerCellStyle={() => ({
					paddingX: 1.5,
					paddingTop: 6,
					paddingBottom: 1,
					backgroundColor: 'common.white',
					color: 'common.black'
				})}
				bodyCellStyle={(cell, _rowIndex, colIndex) => ({
					borderTopLeftRadius: colIndex === 0 ? 24 : 0,
					borderBottomLeftRadius: colIndex === 0 ? 24 : 0,
					borderTopRightRadius: colIndex === cell.row.getAllCells().length - 1 ? 24 : 0,
					borderBottomRightRadius: colIndex === cell.row.getAllCells().length - 1 ? 24 : 0,
					border: 'none'
				})}
			/>
		</>
	);
};
